import { addDoc, collection, deleteDoc, doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';

const MESJES_BOX_INCIDENT_LABEL = 'doosje mesjes afgeleverd';
const MESJES_PER_BOX_DELIVERY = 50;

function normalizeIncidentKey(value) {
  return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

export function resolveTotalMesjesUsed(meldingData) {
  const manualMesjes = Math.max(0, Number(meldingData?.mesjesHandmatig ?? meldingData?.mesjes ?? 0));
  const incidentKeys = new Set(
    (Array.isArray(meldingData?.meldingIncidentFlags) ? meldingData.meldingIncidentFlags : [])
      .map((entry) => normalizeIncidentKey(entry))
      .filter(Boolean)
  );
  const includesDeliveredBox = incidentKeys.has(MESJES_BOX_INCIDENT_LABEL);
  return {
    manualMesjes,
    includesDeliveredBox,
    extraMesjes: includesDeliveredBox ? MESJES_PER_BOX_DELIVERY : 0,
    totalMesjesUsed: manualMesjes + (includesDeliveredBox ? MESJES_PER_BOX_DELIVERY : 0),
  };
}

export async function toggleNotificationsSetting({
  db,
  userId,
  notificationsEnabled,
  notificationPreferences,
  normalizeNotificationPreferences,
}) {
  await setDoc(
    doc(db, 'users', userId),
    {
      notificationsEnabled,
      notificationPreferences: normalizeNotificationPreferences(notificationPreferences),
    },
    { merge: true }
  );
}

export async function toggleNotificationPreferenceSetting({ db, userId, nextPreferences }) {
  await setDoc(
    doc(db, 'users', userId),
    { notificationPreferences: nextPreferences },
    { merge: true }
  );
}

export async function createMeldingEntry({
  db,
  user,
  userBranchId,
  meldingData,
  logger,
  resolveVestigingDocId,
  canonicalizeVestigingId,
  getVestigingIdCandidates,
  getCanonicalRobotName,
  onReload,
}) {
  if (!user) throw new Error('Gebruiker niet ingelogd');
  if (!userBranchId) throw new Error('Geen vestiging geselecteerd');

  const submitStartedAt = typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
  const elapsed = () => {
    const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();
    return Math.max(0, Math.round(now - submitStartedAt));
  };
  const targetVestigingId = (await resolveVestigingDocId(userBranchId)) || userBranchId;

  const bezoekRaw = meldingData?.isRobotBezocht;
  const isRobotBezocht = typeof bezoekRaw === 'boolean'
    ? bezoekRaw
    : String(bezoekRaw ?? meldingData?.robotBezocht ?? 'Ja').trim().toLowerCase() !== 'nee';
  const normaalRaw = meldingData?.isNormaleMelding;
  const isNormaleMelding = typeof normaalRaw === 'boolean'
    ? normaalRaw
    : String(normaalRaw ?? meldingData?.meldingNormaal ?? 'Ja').trim().toLowerCase() !== 'nee';
  const { manualMesjes, includesDeliveredBox, extraMesjes, totalMesjesUsed } = resolveTotalMesjesUsed(meldingData);
  const bladeHeads = Math.max(0, Number(meldingData?.bladeHeads ?? meldingData?.robotBladeConfig?.bladeHeads ?? 0)) || 0;
  const bladesPerHead = Math.max(0, Number(meldingData?.bladesPerHead ?? meldingData?.robotBladeConfig?.bladesPerHead ?? 0)) || 0;
  const totalBlades = Math.max(0, Number(meldingData?.totalBlades ?? meldingData?.robotBladeConfig?.totalBlades ?? (bladeHeads * bladesPerHead))) || 0;

  const canonicalRobotName = getCanonicalRobotName(
    meldingData?.selectedRobot?.robot || meldingData?.selectedRobot || meldingData,
    'Onbekend'
  );
  const selectedRobotId = String(
    meldingData?.selectedRobot?.robot?.id
    || meldingData?.selectedRobot?.id
    || meldingData?.robotId
    || ''
  ).trim() || null;

  const melding = {
    ...meldingData,
    isRobotBezocht,
    robotBezocht: isRobotBezocht ? 'Ja' : 'Nee',
    isNormaleMelding,
    meldingNormaal: isNormaleMelding ? 'Ja' : 'Nee',
    mesjes: totalMesjesUsed,
    mesjesHandmatig: manualMesjes,
    doosjeMesjesAfgeleverd: includesDeliveredBox,
    extraMesjesViaDoosje: extraMesjes,
    bladeHeads,
    bladesPerHead,
    totalBlades,
    robotBladeConfig: {
      bladeHeads,
      bladesPerHead,
      totalBlades,
    },
    userId: user.uid,
    displayName: user.displayName || user.email,
    timestamp: serverTimestamp(),
    meldingType: 'handmatig',
    isAutoMelding: false,
    isManualMelding: true,
    meldingEntryType: 'Manual',
    robotId: selectedRobotId,
    naam: canonicalRobotName,
    robot: canonicalRobotName,
    robotName: canonicalRobotName,
  };

  const writeCandidates = Array.from(new Set([
    targetVestigingId,
    userBranchId,
    canonicalizeVestigingId(userBranchId),
    ...getVestigingIdCandidates(userBranchId),
  ].filter(Boolean)));

  logger.info('MELDING_CREATE', 'Starting melding create', {
    userUid: user?.uid || null,
    userEmail: user?.email || null,
    userBranchId,
    targetVestigingId,
    writeCandidates,
  });

  let ref = null;
  let lastPermissionError = null;
  for (const candidateBranchId of writeCandidates) {
    logger.debug('MELDING_CREATE', `Trying write candidate: ${candidateBranchId}`);
    try {
      ref = await addDoc(collection(db, 'vestigingen', candidateBranchId, 'meldingen'), melding);
      logger.info('MELDING_CREATE', 'Melding write succeeded', {
        candidateBranchId,
        meldingId: ref?.id || null,
      });
      break;
    } catch (err) {
      const code = err?.code || '';
      logger.warn('MELDING_CREATE', 'Melding write failed for candidate', {
        candidateBranchId,
        code,
        message: err?.message || String(err),
      });
      if (code === 'permission-denied') {
        lastPermissionError = err;
        continue;
      }
      throw err;
    }
  }

  if (!ref) {
    const attempted = writeCandidates.join(', ');
    const original = lastPermissionError?.message || 'permission-denied';
    logger.error('MELDING_CREATE', 'All write candidates failed', { attempted, original });
    throw new Error(`Missing or insufficient permissions (${original}). Tried vestigingen: ${attempted}`);
  }

  logger.info('MELDING_CREATE_TIMING', 'Primary melding write confirmed', {
    meldingId: ref.id,
    elapsedMs: elapsed(),
  });

  // Durable derived writes are handled by the existing server-side melding
  // onCreate trigger. Only the UI refresh remains detached from success.
  void new Promise((resolve) => setTimeout(resolve, 0)).then(async () => {
    if (typeof onReload !== 'function') return;
    try {
      await onReload();
    } catch (error) {
      console.warn('Meldingenlijst vernieuwen na submit mislukt:', error);
    }
  });

  return ref;
}

export async function deleteMeldingEntry({
  db,
  auth,
  meldingIdOrObject,
  userBranchId,
  logAuditEvent,
  AuditActions,
  onReload,
  onRefresh,
  skipConfirm = false,
}) {
  const meldingId = typeof meldingIdOrObject === 'string' ? meldingIdOrObject : meldingIdOrObject?.id;
  const vestigingId = typeof meldingIdOrObject === 'object' ? (meldingIdOrObject.vestigingId || meldingIdOrObject.vestiging) : userBranchId;

  if (!skipConfirm && !window.confirm('Weet je zeker dat je deze melding wilt verwijderen?')) return;
  if (!meldingId) throw new Error('Geen melding id');

  let ref = null;
  if (typeof meldingIdOrObject === 'object' && meldingIdOrObject) {
    const path = meldingIdOrObject.path || meldingIdOrObject._path;
    if (path) {
      ref = doc(db, path);
    } else if (meldingIdOrObject.source === 'root') {
      ref = doc(db, 'meldingen', meldingId);
    }
  }

  if (!ref) {
    if (!vestigingId) throw new Error('Geen vestiging geselecteerd');
    ref = doc(db, 'vestigingen', vestigingId, 'meldingen', meldingId);
  }

  await deleteDoc(ref);
  try {
    await logAuditEvent(AuditActions.DELETE_MELDING, 'melding', meldingId, vestigingId, {
      deletedBy: auth.currentUser?.email || auth.currentUser?.displayName || null,
    });
  } catch (error) {
    console.warn('Audit delete melding failed', error);
  }

  if (typeof onReload === 'function') {
    await onReload();
  }
  if (typeof onRefresh === 'function') {
    onRefresh();
  }
}

export async function updateMeldingStatusEntry({
  db,
  auth,
  user,
  meldingId,
  newStatus,
  userBranchId,
  getDisplayName,
  logAuditEvent,
  AuditActions,
  createChangesObject,
  onReload,
}) {
  const isObj = typeof meldingId === 'object' && meldingId;
  const id = isObj ? meldingId.id : meldingId;
  if (!id) throw new Error('Geen melding id');

  const vestigingForUpdate = isObj ? (meldingId.vestigingId || meldingId.vestiging || userBranchId) : userBranchId;

  let ref = null;
  if (isObj) {
    const path = meldingId.path || meldingId._path;
    if (path) {
      ref = doc(db, path);
    } else if (meldingId.source === 'root') {
      ref = doc(db, 'meldingen', id);
    }
  }
  if (!ref) {
    if (!vestigingForUpdate) throw new Error('Geen vestiging geselecteerd');
    ref = doc(db, 'vestigingen', vestigingForUpdate, 'meldingen', id);
  }

  let before = null;
  try {
    const snap = await getDoc(ref);
    if (snap && snap.exists()) before = { id: snap.id, ...snap.data() };
  } catch {
    // The status update remains authoritative if the pre-read is unavailable.
  }

  await updateDoc(ref, {
    status: newStatus,
    statusUpdatedAt: serverTimestamp(),
    technician: getDisplayName(user),
    username: getDisplayName(user),
    email: user.email,
  });

  try {
    const after = { id, status: newStatus };
    await logAuditEvent(
      AuditActions.UPDATE_MELDING,
      'melding',
      id,
      vestigingForUpdate,
      { updatedBy: auth.currentUser?.email || auth.currentUser?.displayName || null, field: 'status', value: newStatus },
      createChangesObject(before, after)
    );
  } catch (error) {
    console.warn('Audit update melding failed', error);
  }

  if (typeof onReload === 'function') {
    await onReload();
  }
}
